import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Mic, Palette } from "lucide-react";
import { api, lyricStanzas } from "../lib/api";
import { Layout } from "../components/Layout";
import { BiniBubble } from "../components/Bini";
import { YouTubePlayer } from "../components/YouTubePlayer";
import { LangBadge } from "../components/Flags";
import { HiddenStar } from "../components/HiddenStar";
import { ColoringCard } from "./Coloring";
import { t } from "../i18n";

export default function SongDetail() {
  const { slug } = useParams();
  const [song, setSong] = useState(null);
  const [missing, setMissing] = useState(false);
  const [colorings, setColorings] = useState([]);
  const playerRef = useRef(null);

  useEffect(() => {
    setSong(null);
    setColorings([]);
    api.get(`/songs/${slug}`).then((r) => {
      setSong(r.data);
      api.get("/colorings", { params: { song_id: r.data.id } }).then((c) => setColorings(c.data));
    }).catch(() => setMissing(true));
  }, [slug]);

  if (missing) return <Layout back="/comptines"><BiniBubble testId="song-not-found">{t("soon.notFound")}</BiniBubble></Layout>;
  if (!song) return <Layout back="/comptines"><div className="h-64" /></Layout>;
  const stanzas = lyricStanzas(song.lyrics);

  return (
    <Layout back="/comptines">
      <div className="flex flex-wrap items-center gap-3">
        <h1 data-testid="song-title" className="text-3xl sm:text-5xl font-extrabold text-white [text-shadow:0_3px_0_rgba(30,42,90,0.45),0_0_14px_rgba(30,42,90,0.45)]">{song.title}</h1>
        <LangBadge lang={song.language} testId="song-detail-lang-badge" />
      </div>
      <div className="mt-6"><YouTubePlayer videoId={song.youtube_id} playerRef={playerRef} /></div>

      <div className="mt-8 grid gap-8 lg:grid-cols-[1.4fr_1fr]">
        <section data-testid="song-lyrics" className="cloud-card puffs p-6 sm:p-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-2xl font-extrabold">{t("songs.lyrics")}</h2>
            {stanzas.length > 0 && (
              <Link to={`/comptines/${song.slug}/karaoke`} data-testid="open-karaoke-btn" className="btn-3d !min-h-[56px]" style={{ "--c": "#FF8A1F" }}>
                <Mic className="w-6 h-6" strokeWidth={2.5} /> {t("home.karaoke")}
              </Link>
            )}
          </div>
          {stanzas.length ? (
            <div className="mt-5 space-y-5 text-lg sm:text-xl leading-relaxed">
              {stanzas.map((st, i) => <p key={i}>{st.map((l, j) => <span key={j} className="block">{l}</span>)}</p>)}
            </div>
          ) : (
            <BiniBubble testId="lyrics-empty" size={64} className="mt-5">{t("songs.noLyrics")}</BiniBubble>
          )}
        </section>
        <section data-testid="song-coloring" className="cloud-card p-6 sm:p-8">
          <HiddenStar id="song-page" className="!absolute right-3 bottom-3" />
          <h2 className="flex items-center gap-2 text-2xl font-extrabold"><Palette className="w-7 h-7 text-[#4CC417]" /> {t("songs.coloring")}</h2>
          {colorings.length ? (
            <div className="mt-5 grid grid-cols-2 gap-3">{colorings.map((c) => <ColoringCard key={c.id} c={c} />)}</div>
          ) : (
            <BiniBubble size={64} className="mt-5" testId="coloring-empty">{t("songs.noColoring")}</BiniBubble>
          )}
        </section>
      </div>
    </Layout>
  );
}
